/* prettier: { "printWidth": 200 } */

import { dataTypes } from './constants';
import env from './named/env';
import brand from './named/brand';

// Package tier enum
export const PACKAGE_TIERS = {
  LITE: 'lite',
  BASIC: 'basic',
  PRO: 'pro',
};

// Helper to create field definition with defaults
const createFieldDefinition = ({ isPublic, type, packageTier = null, isEditable = false }) => ({
  isPublic,
  type,
  package: isEditable ? packageTier || PACKAGE_TIERS.LITE : false,
  isEditable: isEditable,
});

const allFields = {
  // #region environments
  // aws
  [env.AWS.S3.UsesPathPrefixInsteadOfSubDomain]: {
    isPublic: false,
    type: dataTypes.Required.Boolean,
  },
  [env.AWS.S3.Region]: {
    isPublic: true,
    type: dataTypes.Required.String,
  },
  [env.AWS.S3.AccessKeyId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.S3.AccessSecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.S3.BucketName]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.AWS.S3.ClientBucketName]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.S3.Domain]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.UseNicknameAsIdentifier]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Boolean,
  }),
  [env.AWS.Cognito.Region]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.AccessKeyId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.AccessSecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.UserPoolID]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.ClientID]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.Cognito.ClientSecret]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.SES.Region]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.SES.AccessKeyId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.SES.AccessSecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.SES.SenderName]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.SES.SenderEmail]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.AWS.DynamoDB.Region]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.AWS.DynamoDB.AccessKeyId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.AWS.DynamoDB.AccessSecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.AWS.DynamoDB.Tables.SingleProduct]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),

  // firebase
  [env.Firebase.Common.ProjectId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.ProjectKeyId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.AppId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.ApiKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.PrivateKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.ClientEmail]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Common.ClientId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.Analytics.Enabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [env.Firebase.Analytics.MeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.RealtimeDatabase.Region]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.RealtimeDatabase.Name]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Firebase.CloudMessaging.SenderId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),

  // map api
  [env.Map.GoogleMap.Client.ApiKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [env.Map.YandexMap.Client.ApiKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),

  // mobile app
  [env.MobileApp.ForceUpdateVersionIfLessThen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.PRO,
  }),

  // payments
  [env.Payment.AmeriaBank.IsTesting]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Boolean,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.ClientID]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.TwoStepFreeze]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Boolean,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.TestRange.Start]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Number,
    isEditable: true,
  }),
  [env.Payment.AmeriaBank.TestRange.End]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Number,
    isEditable: true,
  }),
  [env.Payment.Yookassa.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Payment.Yookassa.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Payment.YumGo.IsTesting]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Boolean,
    isEditable: true,
  }),
  [env.Payment.YumGo.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.YumGo.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.YumGo.PublicKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.YumGo.PrivateKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.YumGo.ServiceName]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Stripe.OpenWidgetInsteadOfRedirect]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
    isEditable: true,
  }),
  [env.Payment.Stripe.PublishableKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Stripe.SecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDBank.IsTesting]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Boolean,
    isEditable: true,
  }),
  [env.Payment.IDBank.Api.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDBank.Api.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDBank.Binding.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDBank.Binding.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDram.ReceiverAccount]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.IDram.SecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Click.MerchantId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Click.MerchantUserId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Click.ServiceId]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Payment.Click.SecretKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),

  // delivery
  [env.Delivery.HayPost.API.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.HayPost.API.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.HayPost.API.CustomerID]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.MegaMove.API.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.MegaMove.API.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.MegaMove.Vendor.TokenSignature]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.MegaMove.Vendor.Username]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.MegaMove.Vendor.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.ZZLogistics.API.User.FirstName]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.ZZLogistics.API.User.LastName]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.ZZLogistics.API.User.Email]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.ZZLogistics.API.User.Password]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.ZZLogistics.Vendor.AccessToken]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.Yandex.API.AuthToken]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
    isEditable: true,
    packageTier: PACKAGE_TIERS.BASIC,
  }),
  [env.Delivery.CustomDelivery.DeliveryCost]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Number,
    isEditable: true,
    packageTier: PACKAGE_TIERS.LITE,
  }),
  [env.Delivery.CustomDelivery.FreeAfterAmount]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Number,
    isEditable: true,
    packageTier: PACKAGE_TIERS.LITE,
  }),

  // product source
  [env.ProductSource.Providers]: createFieldDefinition({
    isPublic: true,
    isEditable: true,
    type: dataTypes.Required.Array,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.AppKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.AppSecret]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.AccessToken]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.Categories]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.Array,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.SellerInfo.Email]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.SellerInfo.Phone]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.Country]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Ali1688.Language]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Billz.SecretToken]: createFieldDefinition({
    isPublic: false,
    isEditable: true,
    type: dataTypes.Optional.String,
  }),
  [env.ProductSource.ProviderConfigs.Billz.LastSyncedAt]: createFieldDefinition({
    isPublic: true,
    isEditable: true,
    type: dataTypes.Optional.String,
  }),

  // map
  [env.Map.GoogleMap.API.ApiKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.Map.YandexMap.API.GeocoderHttp.ApiKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.Map.YandexMap.API.GeoSuggest.ApiKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),

  // ai
  [env.AI.OpenAI.ApiKey]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.AI.OpenAI.Model]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),
  [env.AI.UsageLimit.ProductSuggestion]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Number,
  }),

  // legal info
  [env.LegalInfo.DaData.AuthToken]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Optional.String,
  }),

  // platforms settings
  [env.PlatformSettings.HomePageProductsCount]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Number,
  }),
  [env.PlatformSettings.ProductPriceIncreasePercent]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Number,
  }),
  [env.PlatformSettings.YandexExpressDeliveryAdditionalCost]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Number,
  }),
  [env.PlatformSettings.Admin.CategoryForm.RadioFormFieldValuesLimit]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
  }),
  [env.PlatformSettings.Seller.ProductCreation.VariationValuesLimit]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
  }),
  [env.PlatformSettings.PrerenderEnabled]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Boolean,
  }),

  // branch.io
  [env.BranchIO.LiveKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.BranchIO.TestKey]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),

  // tracking
  [env.Tracking.Admin.GoogleTag]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Admin.GoogleAnalyticsMeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Admin.MetaPixel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Seller.GoogleTag]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Seller.GoogleAnalyticsMeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Seller.MetaPixel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.WebApp.GoogleTag]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.WebApp.GoogleAnalyticsMeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Tracking.WebApp.MetaPixel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [env.Tracking.Corporate.GoogleTag]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Corporate.GoogleAnalyticsMeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Corporate.MetaPixel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Maas.GoogleTag]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Maas.GoogleAnalyticsMeasurementId]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Tracking.Maas.MetaPixel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [env.Checkout.NotePlaceholder]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  // #endregion

  // #region branding
  // app branding

  // meta
  [brand.MetaTags.Title]: {
    isPublic: true,
    type: dataTypes.Optional.Object,
    isEditable: true,
  },

  [brand.MetaTags.Description]: {
    isPublic: true,
    type: dataTypes.Optional.Object,
    isEditable: true,
  },

  [brand.MetaTags.Keywords]: {
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  },

  [brand.AppBranding.AppName]: {
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  },

  [brand.AppBranding.CompanyTin]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.AppBranding.CompanyType]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.AppBranding.CompanyName]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.AppBranding.ThemeColor]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.AppBranding.AboutUsContent]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.AppBranding.MobileApp.BundleIdentifier]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.AppBranding.MobileApp.IosApp.Name]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.AppBranding.MobileApp.IosApp.ID]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.AppBranding.MobileApp.DeepLinking.Protocols]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),
  [brand.AppBranding.MobileApp.DeepLinking.Domains]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),

  // domains
  [brand.Domain.Assets]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.Domain.Admin]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.Domain.Seller]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.Domain.Webapp]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.Domain.Corporate]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.Domain.Maas]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),

  // country params
  [brand.CountryParam.InitialCountry]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.CountryParam.Currency]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.CountryParam.Locale.Default]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),
  [brand.CountryParam.Locale.List]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.CountryParam.Map.Provider]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.CountryParam.Map.CountryRestrictions]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),
  [brand.CountryParam.Map.InitialZoom]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
  }),
  [brand.CountryParam.Map.InitialCenter.Latitude]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
  }),
  [brand.CountryParam.Map.InitialCenter.Longitude]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
  }),
  [brand.CountryParam.Shipping.ShipFrom]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.CountryParam.Shipping.ShipTo]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.CountryParam.Shipping.DeliveryDateAfterDays]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Number,
  }),

  // contacts
  [brand.Contacts.Addresses]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.Contacts.Phones]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.Contacts.Emails]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
    isEditable: true,
  }),
  [brand.Contacts.NoReplyEmail]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
    isEditable: true,
  }),

  // links
  [brand.Links.MobileApp.GooglePlay]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Links.MobileApp.AppleAppStore]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Links.MobileApp.AppDownloadLink]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Links.Social.Facebook]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [brand.Links.Social.Instagram]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [brand.Links.Social.LinkedIn]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [brand.Links.Social.YouTube]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [brand.Links.Social.Telegram]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
    isEditable: true,
  }),
  [brand.Links.Booking.Calendly]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),

  // feature-flag
  [brand.FeatureFlag.AgeRestricted]: {
    isPublic: true,
    type: dataTypes.Required.Boolean,
  },
  [brand.FeatureFlag.DisableECommerceFlow]: {
    isPublic: true,
    type: dataTypes.Required.Boolean,
  },
  [brand.FeatureFlag.Chat]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.CustomerSupport]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.PhysicalCustomer]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.LegalCustomer]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.LegalCustomerGrant]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.DownloadAppModal]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.Collection]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.CollectionShareEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ContactUs]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AboutUs]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.Collaboration]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ProductWeight]: createFieldDefinition({
    isPublic: true,
    isEditable: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ProductCountry]: createFieldDefinition({
    isPublic: true,
    isEditable: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AdminPushNotifications]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AdminReviewProducts]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AdminBadges]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.DeliveryEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ExpressEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.DeliveryPickupEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ShoppingCartCustomAmount]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ShoppingCartContext]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ProductShare]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ShowProfileFaq]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ShowHelpCenter]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.SellerSinglePageAbility]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.MoneyTransfer]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AllowAdminCustomDeliveryStatuses]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.CustomerBonus]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),

  [brand.FeatureFlag.Auth]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.ShoppingCart]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.AddressSelectionEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.CheckoutDateRangePicker]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.BookingHistoryEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),

  [brand.FeatureFlag.Note]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.SimpleOrderRequest]: createFieldDefinition({
    isPublic: true,
    isEditable: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.FeatureFlag.SimpleCheckoutEnabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),

  [brand.FeatureFlag.BottomTabNavigation]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.PaymentMethods]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.Orders]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.Languages]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.PrivacySecurity]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.PrivacyPolicy]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SharedAccountScreen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SharedBonusScreen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SharedLanguagesScreen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SharedProfileScreen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SharedProfileScreen]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.BonusMutator]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.UploadProductUpdateExcel]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.EditProductAndProductDetail]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.ProductPrice]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.SellerSettingsGalleryTab]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.AdminSellerSupport]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.AdminSellerTab]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.AdminReviewProducts]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.RegisterShowPhoneInput]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.DynamicFilters]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.FeatureFlag.UsesV2ApiFlow]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),

  // ui
  [brand.UI.Theming.Fonts]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),
  [brand.UI.Theming.Theme.Color.Primary]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),

  [brand.UI.Theming.Theme.Override.Gradients]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.Colors]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.Styles]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.Shadows]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.FontSizes]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.Space]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Theming.Theme.Override.Sizes]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Object,
  }),
  [brand.UI.Settings.Common.Product.CallToActionButton]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Object,
  }),
  [brand.UI.Settings.Common.Product.DoNotUseHexColorSelector]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.UI.Settings.MobileApp.SplashScreen.Background]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.UI.Settings.MobileApp.SplashScreen.Foreground]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.UI.Settings.Corporation.DesktopLargeContainer]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.DesktopWhatToSellStyleClassName]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.UI.Settings.Corporation.ShowTopCompanies]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Webapp.Header.SearchVariant]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.String,
  }),
  [brand.UI.Settings.Webapp.Header.UseColoredNavbar]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Webapp.Product.OpenInNewTab]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Webapp.Product.UseSingleProductNewDesign]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Dashboard.DisableProductSearchForSellerAndAdmin]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.AllInOne]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.BusinessVerification]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.BuiltInMarketingTools]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.CustomerSupport]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.ReachRegionalAudience]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.DataDrivenInsightsPersonalization]:
    createFieldDefinition({
      isPublic: true,
      type: dataTypes.Required.Boolean,
    }),
  [brand.UI.Settings.Corporation.BenefitsForSellers.EasyAndIntuitiveDashboard]:
    createFieldDefinition({
      isPublic: true,
      type: dataTypes.Required.Boolean,
    }),
  [brand.UI.Settings.Corporation.WeOfferFeatures.BusinessVerification]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.WeOfferFeatures.BuiltInMarketingTools]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.WeOfferFeatures.CustomerSupport]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),
  [brand.UI.Settings.Corporation.WeOfferFeatures.LogisticsAndLegalSupport]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Boolean,
  }),

  // payment
  [brand.Payment.CustomerMinOrderValue]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Number,
    packageTier: PACKAGE_TIERS.LITE,
  }),
  [brand.Payment.Providers]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),

  // order
  [brand.Order.AutoRejectHours]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Number,
    isEditable: true,
  }),

  // legal info
  [brand.LegalInfo.Provider]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),

  // delivery
  [brand.Delivery.Providers.Enabled]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Required.Array,
  }),
  [brand.Delivery.Providers.Defaults.Pickup]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Delivery.Providers.Defaults.Delivery]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Delivery.Providers.Defaults.Express]: createFieldDefinition({
    isPublic: true,
    type: dataTypes.Optional.String,
  }),
  [brand.Delivery.Configs]: createFieldDefinition({
    isPublic: false,
    type: dataTypes.Required.Object,
  }),
  [brand.Delivery.PickupAddress]: createFieldDefinition({
    isPublic: false,
    isEditable: true,
    type: dataTypes.Optional.Object,
  }),
  // #endregion
};

export const typedFields = Object.entries(allFields).reduce((acc, [key, { type }]) => {
  acc[key] = type;
  return acc;
}, {});

export const accessibility = Object.entries(allFields).reduce(
  (acc, [key, { isPublic }]) => {
    acc[isPublic ? 'public' : 'private'].push(key);
    return acc;
  },
  { public: [], private: [] }
);

export const editableFields = Object.entries(allFields).reduce((acc, [key, value]) => {
  if (value.isEditable) {
    acc[key] = value;
  }
  return acc;
}, {});

export default allFields;
